import { createContext, useContext, type AnchorHTMLAttributes, type ComponentType, type Ref } from 'react';

/**
 * The anchor every internal link in the library renders through.
 *
 * Not in Figma. Reef drew links as native `<a href>` because the prototype used
 * hash routing; in the app a native anchor would reload the page. The library
 * stays router-agnostic: it renders `Link`, and the app injects its router's
 * link through `UIProvider` (`linkComponent`). Stories and parity runs get the
 * default — a plain `<a>` — so geometry is identical either way.
 *
 * External links (citations, `target="_blank"`) keep a raw `<a>`.
 */
export type LinkComponentProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: string;
  ref?: Ref<HTMLAnchorElement>;
};

export type LinkComponent = ComponentType<LinkComponentProps>;

function NativeAnchor(props: LinkComponentProps) {
  // The one deliberate spread in the library: this is a transparent adapter, and
  // content arrives through props.children.
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  return <a {...props} />;
}

export const LinkContext = createContext<LinkComponent>(NativeAnchor);

export function Link(props: LinkComponentProps) {
  const Component = useContext(LinkContext);
  return <Component {...props} />;
}
